import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { whoAmI } from '../../account/sharing';
import { useAuth } from '../../auth/AuthContext';
import { isKnownKind, type ProgramView } from '../../programs/types';
import { KINDS, type ConceptKind } from '../../concepts/kinds';
import { useLearner } from '../../learners/LearnerContext';
import { warmPrograms } from '../../programs/warmSpeech';
import { OTHER_TRACK, setTrack } from '../../programs/track';
import { approveReward, fetchRewardSummary, type RewardSummary } from '../../rewards/rewards';
import {
  addProgramToPackage,
  applyPackage,
  createPackage,
  fetchPackages,
  type ProgramPackage,
} from '../../packages/packages';
import { CATEGORY_ORDER } from '../../concepts/library';
import { fetchMaintenance } from '../../maintenance/maintenance';
import { useSettings } from '../../settings/SettingsContext';
import { translate } from '../../i18n/i18n';

export type PickerMode = 'apply' | 'add' | null;

export function useHome() {
  const { token } = useAuth();
  const [programs, setPrograms] = useState<ProgramView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<ProgramView | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { learners, learnerId } = useLearner();
  const [rewards, setRewards] = useState<RewardSummary | null>(null);
  const [dragging, setDragging] = useState<ProgramView | null>(null);
  const [dueIds, setDueIds] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [packages, setPackages] = useState<ProgramPackage[]>([]);
  const [pickerMode, setPickerMode] = useState<PickerMode>(null);
  const [pickerTarget, setPickerTarget] = useState<ProgramView | null>(null);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [isOwner, setIsOwner] = useState<boolean | null>(null);
  const { settings, loaded: settingsLoaded } = useSettings();
  useEffect(() => {
    if (settingsLoaded) warmPrograms(programs, settings.ttsVoice, settings.ttsRate);
  }, [programs, settingsLoaded, settings.ttsVoice, settings.ttsRate]);

  const load = useCallback(async () => {
    if (!learnerId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await api<ProgramView[]>(
        `/programs?learnerId=${encodeURIComponent(learnerId)}`,
        { token },
      );
      setPrograms(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [token, learnerId]);

  useEffect(() => { load(); }, [load]);

  const loadRewards = useCallback(async () => {
    if (!learnerId) return;
    const s = await fetchRewardSummary(learnerId);
    setRewards(s);
  }, [learnerId]);

  /* Re-read when the account's reward settings change, so switching them off in Settings
     clears the markers here without a reload. */
  useEffect(() => { void loadRewards(); }, [loadRewards, settings.reward]);

  useEffect(() => {
    if (!learnerId) { setDueIds(new Set()); return; }
    let cancelled = false;
    void fetchMaintenance(learnerId).then((m) => {
      if (cancelled) return;
      setDueIds(new Set((m?.programs ?? []).filter((r) => r.due).map((r) => r.programId)));
    });
    return () => { cancelled = true; };
  }, [learnerId, settings.features.maintenanceProbes]);

  async function approve(p: ProgramView) {
    setApproving(p.id);
    setError(null);
    try {
      await approveReward(p.id);
      await loadRewards();
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Could not give that reward.'));
    } finally {
      setApproving(null);
    }
  }

  const loadPackages = useCallback(async () => {
    if (!settings.features.programPackages) { setPackages([]); return; }
    try {
      setPackages(await fetchPackages());
    } catch {
      setPackages([]);
    }
  }, [settings.features.programPackages]);

  useEffect(() => { void loadPackages(); }, [loadPackages]);

  useEffect(() => {
    let cancelled = false;
    void whoAmI()
      .then((w) => { if (!cancelled) setIsOwner(w.ownAccount); })
      .catch(() => { if (!cancelled) setIsOwner(false); });
    return () => { cancelled = true; };
  }, []);

  function openAddToPackage(p: ProgramView) {
    setPickerTarget(p);
    setPickerError(null);
    setPickerMode('add');
  }

  function openApplySet() {
    setPickerError(null);
    setPickerMode('apply');
  }

  function closePicker() {
    if (!pickerBusy) { setPickerMode(null); setPickerTarget(null); }
  }

  async function addToPackage(packageId: string) {
    if (!pickerTarget) return;
    setPickerBusy(true);
    setPickerError(null);
    try {
      await addProgramToPackage(packageId, pickerTarget.id);
      await loadPackages();
      setPickerMode(null);
      setPickerTarget(null);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : translate('That could not be added.'));
    } finally {
      setPickerBusy(false);
    }
  }

  async function createThenAdd(name: string) {
    setPickerBusy(true);
    setPickerError(null);
    try {
      const made = await createPackage(name);
      if (pickerTarget) await addProgramToPackage(made.id, pickerTarget.id);
      await loadPackages();
      setPickerMode(null);
      setPickerTarget(null);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : translate('That set could not be made.'));
    } finally {
      setPickerBusy(false);
    }
  }

  async function applyToLearner(packageId: string) {
    setPickerBusy(true);
    setPickerError(null);
    try {
      await applyPackage(packageId, learnerId);
      await load();
      setPickerMode(null);
    } catch (e) {
      setPickerError(e instanceof Error ? e.message : translate('That set could not be added.'));
    } finally {
      setPickerBusy(false);
    }
  }

  /**
   * Move a program to the other list.
   *
   * <p>The due markers are re-read as well: crossing to maintenance is what starts its review
   * clock, so the row's own marker would otherwise be answering a question about where it used
   * to be.
   */
  async function move(p: ProgramView) {
    setError(null);
    try {
      await setTrack(p.id, OTHER_TRACK[p.track]);
      await load();
      const m = await fetchMaintenance(learnerId);
      setDueIds(new Set((m?.programs ?? []).filter((r) => r.due).map((r) => r.programId)));
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('That program could not be moved.'));
    }
  }

  function requestDelete(id: string) {
    const target = programs.find((p) => p.id === id);
    if (!target) return;
    setError(null);
    setPendingDelete(target);
  }

  function cancelDelete() {
    if (!deleting) setPendingDelete(null);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api<void>(`/programs/${pendingDelete.id}`, { method: 'DELETE', token });
      setPrograms((cur) => cur.filter((p) => p.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Delete failed'));
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  async function copy(source: ProgramView) {
    setError(null);
    try {
      const body = {
        name: translate('{name} copy', { name: source.name }),
        kind: source.kind,
        category: source.category,
        track: source.track,
        /* THE WHOLE CONFIG, COPIED AS IT IS. Naming fields here is what made a
           copy silently lose anything the list did not mention — and with
           parameters differing by kind there is no list that could be right for
           all of them. */
        config: source.config ?? {},
      };
      const created = await api<ProgramView>(
        learnerId ? `/programs?learnerId=${encodeURIComponent(learnerId)}` : '/programs',
        { method: 'POST', token, body },
      );
      setPrograms((cur) =>
        [...cur, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : translate('Copy failed'));
    }
  }

  const categories = [...new Set(programs.map((p) => p.category).filter((c): c is string => !!c))]
    .sort((a, b) => {
      const ia = (CATEGORY_ORDER as string[]).indexOf(a), ib = (CATEGORY_ORDER as string[]).indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
    });
  const kinds = [...new Set(programs.map((p) => p.kind).filter((k): k is string => !!k))]
    .map((k) => ({ id: k, label: isKnownKind(k) ? KINDS[k as ConceptKind].label : k }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const shown = programs.filter((p) =>
    (!categoryFilter || !categories.includes(categoryFilter) || p.category === categoryFilter) &&
    (!kindFilter || !kinds.some((k) => k.id === kindFilter) || p.kind === kindFilter));

  return {
    settings,
    learners,
    learnerId,
    programs,
    shown,
    current: shown.filter((p) => p.track === 'CURRENT'),
    maintenance: shown.filter((p) => p.track === 'MAINTENANCE'),
    categories,
    categoryFilter,
    setCategoryFilter,
    kinds,
    kindFilter,
    setKindFilter,
    loading,
    error,
    rewards,
    approving,
    approve,
    dueIds,
    dragging,
    setDragging,
    move,
    copy,
    requestDelete,
    pendingDelete,
    deleting,
    confirmDelete,
    cancelDelete,
    packages,
    showPackages: settings.features.programPackages && isOwner === true,
    pickerMode,
    pickerTarget,
    pickerBusy,
    pickerError,
    openAddToPackage,
    openApplySet,
    closePicker,
    addToPackage,
    createThenAdd,
    applyToLearner,
  };
}

export type HomeModel = ReturnType<typeof useHome>;
